import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { AccountSelectionService, DspService } from "@front/m19-services";
import { IButtonDirective, IIconComponent, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { finalize } from "rxjs";
import { AjaxError } from "rxjs/ajax";

@Component({
  selector: "app-dsp-deal-refresh-button",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IButtonDirective, TranslocoDirective, IIconComponent],
  template: `
    <button
      *transloco="let t"
      iButton
      type="button"
      variant="ghost"
      size="sm"
      [disabled]="refreshing() || isReadOnly()"
      [loading]="refreshing()"
      (click)="refresh($event)"
    >
      <IIcon name="refresh" />
      {{ refreshing() ? t("dsp-media-selector.refreshing_deals") : t("dsp-media-selector.refresh_deals") }}
    </button>
  `,
})
export class DspDealRefreshButtonComponent {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly dspService = inject(DspService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$);
  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, { initialValue: true });
  readonly refreshing = signal(false);

  refresh(event: Event) {
    event.stopPropagation();
    const dspAdvertiserId = this.accountMarketplace()?.dspAdvertiserId;
    if (!dspAdvertiserId || this.refreshing() || this.isReadOnly()) return;
    this.refreshing.set(true);
    this.dspService
      .refreshDspAdvertiserDeals(dspAdvertiserId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.refreshing.set(false)),
      )
      .subscribe({
        next: (deals) => {
          this.toasterService.success(
            this.translocoService.translate("dsp-media-selector.deals_refreshed", { count: deals.length }),
          );
        },
        error: (error: AjaxError) => {
          this.toasterService.error(error.response?.message ?? error.message);
        },
      });
  }
}
